import { addDays, parseBusinessDate, type BusinessDate } from './business-date.ts';
import type { Period } from './period.ts';

/** Period presets of the global picker (docs/DASHBOARD_SPECIFICATION.md §2). Weeks start on Monday. */
export const PRESETS = [
  'today',
  'yesterday',
  'thisWeek',
  'last7',
  'thisMonth',
  'last30',
  'lastMonth',
  'thisQuarter',
  'custom',
] as const;
export type Preset = (typeof PRESETS)[number];

export const DEFAULT_PRESET: Exclude<Preset, 'custom'> = 'last30';

/** 0 = Monday … 6 = Sunday. */
function weekdayIndex(date: BusinessDate): number {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  return (new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7;
}

const firstOfMonth = (date: BusinessDate): BusinessDate =>
  parseBusinessDate(`${date.slice(0, 8)}01`);

function firstOfPreviousMonth(date: BusinessDate): BusinessDate {
  const [year, month] = date.split('-').map(Number) as [number, number];
  const previous = month === 1 ? [year - 1, 12] : [year, month - 1];
  return parseBusinessDate(`${previous[0]}-${String(previous[1]).padStart(2, '0')}-01`);
}

function firstOfQuarter(date: BusinessDate): BusinessDate {
  const month = Number(date.slice(5, 7));
  const quarterStart = Math.floor((month - 1) / 3) * 3 + 1;
  return parseBusinessDate(`${date.slice(0, 4)}-${String(quarterStart).padStart(2, '0')}-01`);
}

/** The period a preset stands for, relative to `today`. `custom` has no fixed period. */
export function presetPeriod(preset: Preset, today: BusinessDate): Period | null {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const day = addDays(today, -1);
      return { from: day, to: day };
    }
    case 'thisWeek':
      return { from: addDays(today, -weekdayIndex(today)), to: today };
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'thisMonth':
      return { from: firstOfMonth(today), to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'lastMonth': {
      const from = firstOfPreviousMonth(today);
      return { from, to: addDays(firstOfMonth(today), -1) };
    }
    case 'thisQuarter':
      return { from: firstOfQuarter(today), to: today };
    case 'custom':
      return null;
  }
}
